/*
 * sh-armour-cart-rules.js
 * SH Updated: Jun 2026 — per-gender enforcement (men's armour ↔ men's
 *   jackets, women's armour ↔ women's jackets). Fixes the cross-gender
 *   trim bug Joe reported Jun 11: removing a men's jacket from a mixed
 *   basket stripped the LADIES armour instead of the men's.
 *
 * Goldtop's "Elbow & Shoulder Armour (options)" products are sold at
 * £0 when bundled with a jacket. There are two of them, one per gender:
 * Men's (product 8244008714472) and Women's (product 8271249572072).
 * Customers can game this by adding multiple sets, or by removing the
 * jacket and checking out with the free armour alone. This script
 * enforces two rules PER GENDER whenever the cart changes:
 *
 *   1. If no jacket of that gender in cart  → remove that gender's armour.
 *   2. That gender's armour quantity must not exceed the same gender's
 *      jacket quantity. Excess is trimmed from cheapest line items first
 *      so paid upgrades survive over free items.
 *
 * Gender of a jacket line is read from its handle/title (every women's
 * jacket carries a "women"/"ladies" token; men's carry none — verified
 * 20/20 against the live catalog Jun 15 2026). /cart.js does NOT expose
 * product tags, so the admin Men/Women tag is unavailable cart-side.
 *
 * Back Armour products are intentionally unrestricted and never touched.
 *
 * Detection strategy: wrap window.fetch and XMLHttpRequest so we
 * observe every /cart/(add|change|update|clear).js call regardless of
 * source. iCart (the drawer app) hijacks the theme's add-to-cart and
 * uses XHR with ?ref_icart=true; Impulse's own paths use fetch. The
 * wrappers cover both.
 *
 * Drawer refresh: after a server-side change, we fire ONLY Impulse's
 * standard cart events (cart:build, cart:refresh). We do NOT touch
 * iCart's DOM directly — that pattern (see history commits 539c426 /
 * 999c3f3 / 8fbadb1) was the May 13 LIVE regression source: removing
 * the .icart-bottom-new-checkout element took the checkout button with
 * it (P2.A) and froze the drawer (P2.B). We accept a slightly stale
 * visual state in exchange for not driving iCart through its UI.
 * Server state is always correct — checkout can't move a stale line
 * through because the line no longer exists in /cart.js.
 *
 * GATE: this script is wrapped in a 3-state activation gate so it can
 * be deployed to LIVE for real-conditions testing without changing
 * customer behaviour:
 *
 *   off            → script returns immediately. Zero behaviour change.
 *                    Customers see LIVE's current cart flow unchanged.
 *   observe        → script runs computeChanges and logs what it WOULD
 *                    do, but applyChange is a no-op (no /cart/change.js
 *                    calls) and refreshUi is skipped. Validates the
 *                    enforcement math against real LIVE traffic patterns
 *                    without mutating any customer's cart.
 *   active (default since Jun 11 2026) → full enforcement mode.
 *
 * Activate per-session via ?sh_p2=observe or ?sh_p2=active in the URL,
 * or persist via localStorage.setItem('sh_p2', 'active'). The default
 * remains 'off' for everyone else — so deploying this file to LIVE is
 * safe; only flagged sessions see any difference.
 *
 * Site-wide rollout (Jun 11 2026): the gate default was flipped from
 * 'off' to 'active' after full live verification — the armour rules now
 * enforce for ALL customers by default. The 3-state gate stays in place:
 * ?sh_p2=off (or localStorage 'off') disables enforcement for a single
 * session, and reverting the default to 'off' disables it site-wide.
 */

(function () {
  var MODE = (function () {
    try {
      var p = new URLSearchParams(window.location.search).get('sh_p2');
      if (p === 'active' || p === 'observe' || p === 'off') {
        // Auto-persist URL flag to localStorage so navigating between pages
        // (e.g. PDP → cart → checkout) keeps the script active without
        // requiring the flag in every URL. Persists for the rest of the
        // browser session. Clear via localStorage.removeItem('sh_p2').
        try { localStorage.setItem('sh_p2', p); } catch (e) {}
        return p;
      }
      var ls = localStorage.getItem('sh_p2');
      if (ls === 'active' || ls === 'observe' || ls === 'off') return ls;
    } catch (e) {}
    // SH Updated Jun 11 2026 — site-wide rollout. Gate default flipped
    // off → active after full live verification: P1 + P2 confirmed on the
    // main theme (#193418592638), exploit blocked at checkout, no checkout/
    // drawer regression. The armour rules now enforce for ALL customers by
    // default. To disable site-wide, revert this to 'off' and re-push.
    return 'active';
  })();
  if (MODE === 'off') return;

  var OBSERVE = MODE === 'observe';

  function log() {
    var args = Array.prototype.slice.call(arguments);
    args.unshift('[SH-P2:' + MODE + ']');
    console.log.apply(console, args);
  }
  log('Activated. MODE=' + MODE + '. ' + (OBSERVE
    ? 'Will compute + log decisions; will NOT mutate cart or fire refresh events.'
    : 'Full enforcement on cart events.'));

  var MENS_ARMOUR_ID = 8244008714472;   // Elbow & Shoulder Armour (options)
  var WOMENS_ARMOUR_ID = 8271249572072; // Women's Elbow & Shoulder Armour (options)
  var RESTRICTED_PRODUCT_IDS = [MENS_ARMOUR_ID, WOMENS_ARMOUR_ID];
  var JACKET_PRODUCT_TYPE = 'Jacket';
  // Women's jackets carry a "women"/"ladies" token in handle/title;
  // men's jackets carry none. This is the only gender signal that
  // survives onto the /cart.js line (tags do not).
  var WOMENS_RE = /women|ladies/i;
  var ICART_SELECTOR = '[class*="icart"], #icart-drawer, .icart, .icart-drawer';
  var CART_API_RE = /\/cart\/(add|change|update|clear)\.js/;
  var CART_ADD_RE = /\/cart\/add\.js/;

  // P2.C bfcache add-blocker
  //
  // May 25 bare-LIVE repro confirmed: clicking checkout → back fires
  // an unwanted /cart/add.js for an already-in-cart item, incrementing
  // its quantity. Repro happened with ZERO of our code on LIVE, so
  // the source is iCart's own bfcache re-sync (or another installed
  // app with similar behaviour). We can't fix iCart; we can intercept.
  //
  // Mechanism: when the browser restores a page from bfcache (back
  // button after navigating away), pageshow fires with persisted=true.
  // We open a 1-second window during which any /cart/add.js call is
  // blocked — fetch returns a synthetic empty-items 200; XHR aborts.
  // Customer-initiated adds during this window are extremely unlikely
  // (they just clicked back, taking time to read the cart) — the
  // false-positive risk is negligible against the false-negative cost
  // of letting bfcache adds through.
  var bfcacheRestoreUntil = 0;
  window.addEventListener('pageshow', function (event) {
    if (event.persisted === true) {
      bfcacheRestoreUntil = Date.now() + 1000;
      log('pageshow: bfcache restore detected — blocking /cart/add.js for 1000ms');
    }
  });
  function inBfcacheWindow() { return Date.now() < bfcacheRestoreUntil; }

  function isRestrictedArmour(item) {
    return RESTRICTED_PRODUCT_IDS.indexOf(item.product_id) !== -1;
  }
  function isJacket(item) {
    return item.product_type === JACKET_PRODUCT_TYPE;
  }
  // 'M' or 'W' for a restricted-armour line — keyed off product_id
  // (the two add-on products are themselves gender-specific).
  function armourGender(item) {
    return item.product_id === WOMENS_ARMOUR_ID ? 'W' : 'M';
  }
  // 'M' or 'W' for a jacket line — read from handle/title (see WOMENS_RE).
  // Defaults to 'M' when no women's token is present.
  function jacketGender(item) {
    var s = (item.handle || '') + ' ' + (item.product_title || item.title || '');
    return WOMENS_RE.test(s) ? 'W' : 'M';
  }

  // Per-gender enforcement. Men's armour is capped against men's jackets
  // and women's armour against women's jackets — independently — so
  // removing a jacket of one gender never trims the other gender's armour.
  function computeChanges(cart) {
    var jackets = { M: 0, W: 0 };
    var armour = { M: [], W: [] };
    for (var i = 0; i < cart.items.length; i++) {
      var it = cart.items[i];
      if (isJacket(it)) jackets[jacketGender(it)] += it.quantity;
      else if (isRestrictedArmour(it)) armour[armourGender(it)].push(it);
    }
    if (!armour.M.length && !armour.W.length) return [];

    var changes = [];
    ['M', 'W'].forEach(function (g) {
      var list = armour[g];
      if (!list.length) return;
      var jacketQty = jackets[g];

      // No jacket of this gender → strip all of this gender's armour.
      if (jacketQty === 0) {
        for (var j = 0; j < list.length; j++) {
          changes.push({ key: list[j].key, variantId: list[j].variant_id, quantity: 0, title: list[j].title });
        }
        return;
      }

      var total = 0;
      for (var k = 0; k < list.length; k++) total += list[k].quantity;
      if (total <= jacketQty) return;

      // Trim the excess within this gender bucket, cheapest line first so
      // paid upgrades survive over free (£0) items.
      var sorted = list.slice().sort(function (a, b) { return a.price - b.price; });
      var excess = total - jacketQty;
      for (var l = 0; l < sorted.length && excess > 0; l++) {
        var item = sorted[l];
        var reduceBy = Math.min(item.quantity, excess);
        excess -= reduceBy;
        changes.push({ key: item.key, variantId: item.variant_id, quantity: item.quantity - reduceBy, title: item.title });
      }
    });
    return changes;
  }

  function applyChange(change) {
    if (OBSERVE) {
      log('would change "' + (change.title || change.key) + '" → qty=' + change.quantity);
      return Promise.resolve({});
    }
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: change.key, quantity: change.quantity }),
    }).then(function (r) { return r.json(); });
  }

  // Drawer refresh — fire Impulse's documented cart events ONLY.
  // No DOM manipulation: the May 13 regression was caused by removing
  // .icart-bottom-new-checkout (P2.A) and freezing iCart's event
  // handlers via removeChild (P2.B). iCart self-refreshes on its next
  // user interaction via its own event handlers. Server state is
  // authoritative — checkout cannot push through a stale line.
  //
  // Removed deliberately (was in the broken version):
  //   - the iCart-targeted event fan (cart:updated, iCart:refresh,
  //     icart:refresh, icart:reload) — H2 candidate for P2.C (could
  //     fan-trigger upsell apps into adding lines).
  //   - the document.querySelectorAll('.ic-cart-item,
  //     .icart-bottom-new-checkout') DOM cleanup block — root cause
  //     of P2.A + P2.B.
  //   - the programmatic .icart-delete-btn click — same root.
  function refreshUi(applied) {
    if (OBSERVE) {
      log('would refresh UI for', applied);
      return;
    }
    document.dispatchEvent(new CustomEvent('cart:build'));
    document.dispatchEvent(new CustomEvent('cart:refresh'));
    try {
      if (window.iCart && typeof window.iCart.refresh === 'function') window.iCart.refresh();
      if (window.iCart && typeof window.iCart.fetchCart === 'function') window.iCart.fetchCart();
      if (window.iCartDrawer && typeof window.iCartDrawer.refresh === 'function') window.iCartDrawer.refresh();
    } catch (e) { /* swallow */ }
  }

  // Re-entry guard. Our own /cart/change.js calls re-fire the cart
  // events and the fetch wrapper, so without this we'd loop forever.
  var running = false;
  var rerunQueued = false;

  function enforce() {
    if (running) { rerunQueued = true; return Promise.resolve(); }
    running = true;
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var changes = computeChanges(cart);
        if (!changes.length) {
          log('cart OK — jackets=' + cart.items.filter(isJacket).reduce(function (s, i) { return s + i.quantity; }, 0) + ', no changes needed');
          return null;
        }
        log('enforce — cart has violation, ' + changes.length + ' change(s) computed');
        return changes.reduce(function (p, c) {
          return p.then(function () { return applyChange(c); });
        }, Promise.resolve()).then(function () { return changes; });
      })
      .then(function (applied) {
        if (applied && applied.length) refreshUi(applied);
      })
      .catch(function (err) {
        log('enforce failed:', err && err.message ? err.message : err);
      })
      .then(function () {
        running = false;
        if (rerunQueued) {
          rerunQueued = false;
          setTimeout(enforce, 50);
        }
      });
  }

  var debounceTimer = null;
  function debouncedEnforce() {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(function () {
      debounceTimer = null;
      enforce();
    }, 120);
  }

  // Impulse events (covers theme-native add-to-cart flows).
  document.addEventListener('cart:updated', debouncedEnforce);
  document.addEventListener('cart:build', debouncedEnforce);
  document.addEventListener('ajaxProduct:added', debouncedEnforce);

  // iCart drawer interactions (quantity stepper, remove button).
  function maybeICart(evt) {
    var t = evt.target;
    if (!t || !t.closest) return;
    if (!t.closest(ICART_SELECTOR)) return;
    setTimeout(debouncedEnforce, 300);
  }
  document.addEventListener('click', maybeICart, true);
  document.addEventListener('change', maybeICart, true);

  // Initial pass — catches a stale cart on back/forward navigation.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', enforce);
  } else {
    enforce();
  }

  // fetch wrapper — Impulse + any modern drawer app uses fetch.
  // Also enforces the bfcache add-block for fetch-driven /cart/add.js.
  try {
    var origFetch = window.fetch;
    if (origFetch) {
      window.fetch = function (input) {
        var url = '';
        try {
          url = typeof input === 'string' ? input : (input && input.url) || '';
        } catch (e) { /* swallow */ }
        if (CART_ADD_RE.test(url) && inBfcacheWindow()) {
          log('BLOCKED fetch /cart/add.js during bfcache restore window:', url);
          return Promise.resolve(new Response(
            '{"items":[],"item_count":0}',
            { status: 200, headers: { 'Content-Type': 'application/json' } }
          ));
        }
        var p = origFetch.apply(this, arguments);
        if (CART_API_RE.test(url)) {
          p.then(debouncedEnforce, debouncedEnforce);
        }
        return p;
      };
    }
  } catch (e) { /* keep going if wrapping fails */ }

  // XHR wrapper — iCart uses XHR with ?ref_icart=true.
  // Also enforces the bfcache add-block for XHR-driven /cart/add.js
  // (the most likely path — iCart's bfcache re-sync per May 25 repro).
  try {
    var origOpen = XMLHttpRequest.prototype.open;
    var origSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url) {
      try { this.__shCartUrl = url || ''; } catch (e) { /* swallow */ }
      return origOpen.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function () {
      var self = this;
      if (self.__shCartUrl && CART_ADD_RE.test(self.__shCartUrl) && inBfcacheWindow()) {
        log('BLOCKED XHR /cart/add.js during bfcache restore window:', self.__shCartUrl);
        // Abort the XHR before send completes. iCart's onload won't
        // fire; onabort/onerror may. iCart should handle gracefully —
        // a transient error during bfcache restore has no user-visible
        // surface (customer just sees the cart they expected).
        setTimeout(function () {
          try { self.abort(); } catch (e) { /* swallow */ }
        }, 0);
        return;
      }
      if (self.__shCartUrl && CART_API_RE.test(self.__shCartUrl)) {
        self.addEventListener('loadend', debouncedEnforce);
      }
      return origSend.apply(this, arguments);
    };
  } catch (e) { /* keep going if wrapping fails */ }
})();
